import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';

interface WxAccount {
  id: number;
  tenant_id: number;
  wx_account_id: number;
  appid: string | null;
  display_name: string | null;
}

interface AdminUser {
  id: number;
  tenant_id: number;
  username: string;
  role: 'admin' | 'super';
  last_login_at: string | null;
  created_at: string;
}

interface Tenant {
  id: number;
  name: string;
  status: string;
  created_at: string;
  wx_accounts: WxAccount[];
  admins: AdminUser[];
}

export default function SuperAdminPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<{ items: Tenant[] }>({
    queryKey: ['tenants'],
    queryFn: () => api.get('/api/tenants'),
  });

  const createTenant = useMutation({
    mutationFn: (name: string) => api.post('/api/tenants', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });

  const [newName, setNewName] = useState('');

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">超管 · 租户管理</h2>
          <p className="mt-1 text-sm text-slate-500">创建租户、绑定主系统公众号、为租户开通管理员账号。</p>
        </div>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newName.trim()) return;
            createTenant.mutate(newName.trim(), { onSuccess: () => setNewName('') });
          }}
        >
          <label className="text-sm">
            <span className="mb-1 block text-slate-600">新增租户</span>
            <input
              className="input"
              placeholder="租户名称"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
          <button className="btn-primary" type="submit" disabled={createTenant.isPending}>
            {createTenant.isPending ? '创建中...' : '创建'}
          </button>
        </form>
      </div>

      {createTenant.error && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {(createTenant.error as ApiError).message}
        </div>
      )}
      {error && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{(error as Error).message}</div>
      )}
      {isLoading && <div className="text-slate-500">加载中...</div>}

      <div className="space-y-4">
        {(data?.items ?? []).map((t) => (
          <TenantCard key={t.id} tenant={t} />
        ))}
        {!isLoading && (data?.items ?? []).length === 0 && (
          <div className="card text-sm text-slate-500">还没有任何租户。先用上面的表单创建一个吧。</div>
        )}
      </div>
    </div>
  );
}

function TenantCard({ tenant }: { tenant: Tenant }) {
  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-base font-semibold text-slate-800">{tenant.name}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            #{tenant.id} · {tenant.status} · 创建于 {tenant.created_at}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <WxAccountsPanel tenant={tenant} />
        <AdminsPanel tenant={tenant} />
      </div>
    </div>
  );
}

function WxAccountsPanel({ tenant }: { tenant: Tenant }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ wx_account_id: '', appid: '', display_name: '' });

  const bind = useMutation({
    mutationFn: () =>
      api.post(`/api/tenants/${tenant.id}/wx-accounts`, {
        wx_account_id: Number(form.wx_account_id),
        appid: form.appid || undefined,
        display_name: form.display_name || undefined,
      }),
    onSuccess: () => {
      setForm({ wx_account_id: '', appid: '', display_name: '' });
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  const unbind = useMutation({
    mutationFn: (wxAccountId: number) => api.del(`/api/tenants/${tenant.id}/wx-accounts/${wxAccountId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });

  return (
    <section className="rounded-md border border-slate-100 bg-slate-50/40 p-3">
      <h3 className="text-sm font-medium text-slate-700">绑定公众号</h3>
      <ul className="mt-2 space-y-1 text-sm">
        {tenant.wx_accounts.map((a) => (
          <li key={a.id} className="flex items-center justify-between rounded bg-white px-2 py-1">
            <span>
              <span className="text-slate-700">{a.display_name || a.appid || `#${a.wx_account_id}`}</span>
              <span className="ml-2 text-xs text-slate-400">主系统 ID {a.wx_account_id}</span>
            </span>
            <button
              className="text-xs text-red-600 hover:underline disabled:opacity-50"
              onClick={() => unbind.mutate(a.wx_account_id)}
              disabled={unbind.isPending}
            >
              解绑
            </button>
          </li>
        ))}
        {tenant.wx_accounts.length === 0 && <li className="text-xs text-slate-400">尚未绑定。</li>}
      </ul>
      <form
        className="mt-3 space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.wx_account_id) return;
          bind.mutate();
        }}
      >
        <input
          className="input"
          placeholder="主系统 wx_account_id（必填）"
          value={form.wx_account_id}
          onChange={(e) => setForm({ ...form, wx_account_id: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            className="input"
            placeholder="AppID（可选）"
            value={form.appid}
            onChange={(e) => setForm({ ...form, appid: e.target.value })}
          />
          <input
            className="input"
            placeholder="显示名（可选）"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
        </div>
        <button className="btn-primary w-full" type="submit" disabled={bind.isPending}>
          {bind.isPending ? '绑定中...' : '绑定公众号'}
        </button>
        {bind.error && (
          <div className="text-xs text-red-600">{(bind.error as ApiError).message}</div>
        )}
      </form>
    </section>
  );
}

function AdminsPanel({ tenant }: { tenant: Tenant }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<{ username: string; password: string; role: 'admin' | 'super' }>({
    username: '',
    password: '',
    role: 'admin',
  });

  const create = useMutation({
    mutationFn: () =>
      api.post(`/api/tenants/${tenant.id}/admins`, {
        username: form.username.trim(),
        password: form.password,
        role: form.role,
      }),
    onSuccess: () => {
      setForm({ username: '', password: '', role: 'admin' });
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  return (
    <section className="rounded-md border border-slate-100 bg-slate-50/40 p-3">
      <h3 className="text-sm font-medium text-slate-700">管理员</h3>
      <ul className="mt-2 space-y-1 text-sm">
        {tenant.admins.map((a) => (
          <li key={a.id} className="flex items-center justify-between rounded bg-white px-2 py-1">
            <span>
              <span className="text-slate-700">{a.username}</span>
              {a.role === 'super' && (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">超管</span>
              )}
            </span>
            <span className="text-xs text-slate-400">
              {a.last_login_at ? `上次登录 ${a.last_login_at}` : '从未登录'}
            </span>
          </li>
        ))}
        {tenant.admins.length === 0 && <li className="text-xs text-slate-400">还没有管理员。</li>}
      </ul>
      <form
        className="mt-3 space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.username.trim() || !form.password) return;
          create.mutate();
        }}
      >
        <input
          className="input"
          placeholder="用户名"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
        />
        <input
          className="input"
          type="password"
          placeholder="初始密码"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <select
          className="input"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'super' })}
        >
          <option value="admin">普通管理员</option>
          <option value="super">超管</option>
        </select>
        <button className="btn-primary w-full" type="submit" disabled={create.isPending}>
          {create.isPending ? '创建中...' : '创建管理员'}
        </button>
        {create.error && (
          <div className="text-xs text-red-600">{(create.error as ApiError).message}</div>
        )}
      </form>
    </section>
  );
}
